// First-run text-size prompt. Shows once when the user has not yet made
// an explicit text-size choice (me.accessibility.textSizeChosen === false
// or absent). Three buttons, live preview, persists via the same
// /api/me/accessibility PATCH the Settings page uses, and marks
// textSizeChosen=true so we never ask twice.
//
// Focus-group context: #13 in qa/focus-group/FIX_LIST.md. Doris (78),
// Earl (74), Gloria (82), Margaret (76), Rosa (71) all asked for a
// larger default and noted that the existing Settings → Text size flow
// was three taps too deep.

import { useEffect, useState } from 'react';
import { useAuthedFetch } from '../hooks/useAuthedFetch';
import { type Me, useMe } from '../hooks/useMe';

type FontSize = 'base' | 'lg' | 'xl';

const LABEL: Record<FontSize, string> = {
  base: 'Normal',
  lg: 'Larger',
  xl: 'Largest',
};

const PREVIEW_PX: Record<FontSize, number> = {
  base: 18,
  lg: 22,
  xl: 26,
};

const HTML_PX: Record<FontSize, string> = {
  base: '18px',
  lg: '22px',
  xl: '26px',
};

function applyToHtml(size: FontSize): void {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty('--font-size-base', HTML_PX[size]);
}

export function TextSizeFirstRun(): JSX.Element | null {
  const authedFetch = useAuthedFetch();
  const { me } = useMe();
  const [preview, setPreview] = useState<FontSize>('lg');
  const [dismissed, setDismissed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (me?.accessibility?.fontSize) setPreview(me.accessibility.fontSize);
  }, [me]);

  if (!me) return null;
  if (dismissed) return null;
  if (me.accessibility?.textSizeChosen === true) return null;

  const choose = async (size: FontSize) => {
    setSaving(true);
    applyToHtml(size);
    try {
      await authedFetch('/api/me/accessibility', {
        method: 'PATCH',
        body: JSON.stringify({ fontSize: size, textSizeChosen: true }),
      });
    } catch {
      // best-effort; we already applied locally
    } finally {
      setSaving(false);
      setDismissed(true);
    }
  };

  const previewFn = (size: FontSize) => {
    setPreview(size);
    applyToHtml(size);
  };

  return (
    <section
      aria-labelledby="text-size-prompt-title"
      style={{
        background: 'var(--color-surface-secondary)',
        border: '1.5px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-md)',
        margin: '0 0 var(--space-md)',
      }}
    >
      <h3 id="text-size-prompt-title" style={{ margin: '0 0 8px', fontSize: 20 }}>
        What size text feels best?
      </h3>
      <p style={{ margin: '0 0 12px', fontSize: 16, color: 'var(--color-text-secondary)' }}>
        Pick a size. You can change this any time in Settings.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        {(['base', 'lg', 'xl'] as FontSize[]).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => previewFn(s)}
            aria-pressed={preview === s}
            className={`symptom-pill${preview === s ? ' is-active' : ''}`}
            style={{ minHeight: 48, minWidth: 100 }}
          >
            {LABEL[s]}
          </button>
        ))}
      </div>
      <p
        aria-live="polite"
        style={{
          fontSize: PREVIEW_PX[preview],
          margin: '0 0 12px',
          padding: 12,
          background: 'var(--color-surface-primary)',
          borderRadius: 'var(--radius-sm)',
        }}
      >
        This is how text will look at the {LABEL[preview].toLowerCase()} size.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <button
          type="button"
          onClick={() => void choose(preview)}
          disabled={saving}
          className="home-hero__cta"
          style={{ minHeight: 48 }}
        >
          Save this size
        </button>
      </div>
    </section>
  );
}

// Exported for tests / future call sites that want to know whether the
// prompt is going to render before mounting it.
export function shouldShowTextSizePrompt(me: Me | null): boolean {
  if (!me) return false;
  return me.accessibility?.textSizeChosen !== true;
}
